import React, { useState, useEffect, useCallback } from 'react'

import themes from 'devextreme/ui/themes'
import { currentTheme as ct, refreshTheme } from 'devextreme/viz/themes'

import { useSettingsUpdate } from 'hooks/use-settings-update'

const themeClasses: {[k: string]: string} = {
    'material.blue.light.compact': 'theme-blue-light',
    'material.blue.light.ix1.compact': 'theme-blue-light-ix1',
    'material.lime.light.compact': 'theme-lime-light',
    'material.lime.light.ix1.compact': 'theme-lime-light-ix1',
    'material.orange.light.compact': 'theme-orange-light',
    'material.orange.light.ix1.compact': 'theme-orange-light-ix1',
    'material.orange.dark.compact': 'theme-orange-dark',
    'material.orange.dark.ix1.compact': 'theme-orange-dark-ix1',
    'material.orange.dark.ix2.compact': 'theme-orange-dark-ix2',
    'material.lime.dark.compact': 'theme-lime-dark',
    'material.lime.dark.ix1.compact': 'theme-lime-dark-ix1',
    'material.lime.dark.ix2.compact': 'theme-lime-dark-ix2',
    'material.blue.dark.compact': 'theme-blue-dark',
    'material.blue.dark.ix1.compact': 'theme-blue-dark-ix1',
    'material.blue.dark.ix2.compact': 'theme-blue-dark-ix2'
  }

export const themesList = [
    {
        id: 1,
        name: 'Material Blue Light Compact',
        dxName: 'material.blue.light.compact',
        imgSrc: `/images/material.blue.light.compact.svg`,
        category: 'light',
    },
    {
        id: 2,
        name: 'Material Blue Light iX1 Compact',
        dxName: 'material.blue.light.ix1.compact',
        imgSrc: `/images/material.blue.light.compact.svg`,
        category: 'light',
    },
    {
        id: 3,
        name: 'Material Orange Dark Compact',
        dxName: 'material.orange.dark.compact',
        imgSrc: `/images/material.orange.dark.compact.svg`,
        category: 'dark',
    },
    {
        id: 4,
        name: 'Material Orange Dark iX1 Compact',
        dxName: 'material.orange.dark.ix1.compact',
        imgSrc: `/images/material.orange.dark.compact.svg`,
        category: 'dark',
    },
    {
        id: 5,
        name: 'Material Orange Dark iX2 Compact',
        dxName: 'material.orange.dark.ix2.compact',
        imgSrc: `/images/material.orange.dark.compact.svg`,
        category: 'dark',
    },
    {
        id: 6,
        name: 'Material Lime Light Compact',
        dxName: 'material.lime.light.compact',
        imgSrc: `/images/material.lime.light.compact.svg`,
        category: 'light',
    },
    {
        id: 7,
        name: 'Material Lime Light Compact IX1',
        dxName: 'material.lime.light.ix1.compact',
        imgSrc: `/images/material.lime.light.compact.svg`,
        category: 'light',
    },
    {
        id: 8,
        name: 'Material Orange Light Compact',
        dxName: 'material.orange.light.compact',
        imgSrc: `/images/material.orange.light.compact.svg`,
        category: 'light',
    },
    {
        id: 9,
        name: 'Material Orange Light Compact IX1',
        dxName: 'material.orange.light.ix1.compact',
        imgSrc: `/images/material.orange.light.compact.svg`,
        category: 'light',
    },
    {
        id: 10,
        name: 'Material Lime Dark Compact',
        dxName: 'material.lime.dark.compact',
        imgSrc: `/images/material.lime.dark.compact.svg`,
        category: 'dark',
    },
    {
        id: 11,
        name: 'Material Lime Dark iX1 Compact',
        dxName: 'material.lime.dark.ix1.compact',
        imgSrc: `/images/material.lime.dark.compact.svg`,
        category: 'dark',
    },
    {
        id: 12,
        name: 'Material Lime Dark iX2 Compact',
        dxName: 'material.lime.dark.ix2.compact',
        imgSrc: `/images/material.lime.dark.compact.svg`,
        category: 'dark',
    },
    {
        id: 13,
        name: 'Material Blue Dark Compact',
        dxName: 'material.blue.dark.compact',
        imgSrc: `/images/material.blue.dark.compact.svg`,
        category: 'dark',
    },
    {
        id: 14,
        name: 'Material Blue Dark iX1 Compact',
        dxName: 'material.blue.dark.ix1.compact',
        imgSrc: `/images/material.blue.dark.compact.svg`,
        category: 'dark',
    },
    {
        id: 15,
        name: 'Material Blue Dark iX2 Compact',
        dxName: 'material.blue.dark.ix2.compact',
        imgSrc: `/images/material.blue.dark.compact.svg`,
        category: 'dark',
    },
]

const getLocalTheme = () => {

    return themes.current()
}

type ContextProps = {
    currentTheme: string | null,
    themeClass: string | null,
    changeTheme: (theme: string, updateSettings?: boolean) => void
}

/* v8 ignore next */
const IxThemeContext = React.createContext<ContextProps>({ currentTheme: '', themeClass: '', changeTheme: () => null })

function IxThemeProvider(props: any) {
    const [currentTheme, setCurrentTheme] = useState<string>(themes.current())
    const [themeClass, setThemeClass] = useState<string>(themeClasses[currentTheme])
    const updateSetting = useSettingsUpdate()

    useEffect(() => {
        const id = localStorage.getItem('ct')
        if (id) {
            const themeData = themesList.find(t => t.id === Number(id))
            if (themeData) {
                setCurrentTheme(themeData.dxName)
                setThemeClass(themeClasses[themeData.dxName])
                themes.current(themeData.dxName)
                ct(themeData.dxName)
                refreshTheme()
                document.querySelector('html')?.classList.add(themeClasses[themeData.dxName])

                return
            }
        }

        document.querySelector('html')?.classList.add(themeClass)
    }, []) // eslint-disable-line

    const changeTheme = useCallback((theme: string, updateSettings=true): void => {
        const oldClass = themeClass

        const themeData = themesList.find(t => t.dxName === theme)

        // if theme is not found don't do nothing
        if (!themeData) { return }

        // update local state
        setCurrentTheme(theme)
        setThemeClass(themeClasses[theme])

        // send update to backend with the new theme setting
        updateSettings && updateSetting('theme', themeData.id)
        localStorage.setItem('ct', themeData.id.toString())

        // tell Dx to use the new theme
        themes.current(theme)
        ct(theme)
        refreshTheme()

        // set correct theme class to <body> for out custom styles
        document.querySelector('html')?.classList.remove(oldClass)
        document.querySelector('html')?.classList.add(themeClasses[theme])
    }, [themeClass, updateSetting])

    return (
        <IxThemeContext.Provider value={{ currentTheme, themeClass, changeTheme }} {...props}>
            {props.children}
        </IxThemeContext.Provider>
    )
}

const useIxTheme = () => React.useContext(IxThemeContext)

export {
    IxThemeProvider,
    useIxTheme
}
